import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type {
  MessageReadMarker,
  PlatformBroadcastMessage,
  TenantConversation,
  TenantMessage,
} from '@quality-vet/contracts';

import { Modal } from '../components/modal';
import { StatusMessage } from '../components/status-message';
import {
  formatDate,
  isAbortError,
  readApiError,
  supersededRequestError,
  toIso,
} from '../utils/communication-helpers';
import '../components/app-utility-bar.css';

export type MessagesPanelMember = {
  userId: string;
  email: string;
  status?: 'invited' | 'active' | 'disabled';
};

export type MessagesPanelProps = {
  apiUrl: string;
  accessToken: string;
  tenantId: string;
  tenantName?: string;
  currentUserId: string;
  currentUserEmail?: string;
  members?: MessagesPanelMember[];
  isPlatformAdmin: boolean;
  tenantRoles?: string[];
  timeZone?: string;
  initialConversationId?: string;
  onInitialConversationConsumed?: () => void;
  onViewed?: () => void;
};

type ApiRequest = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
};

function searchIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="m15 15 4 4" />
    </svg>
  );
}

function sendIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="m3.5 11.5 17-8-6.25 17-2.75-6.25-8-2.75Z" />
      <path d="m11.5 14.25 3.25-4" />
    </svg>
  );
}

function initials(value: string): string {
  const segments = value.split(/[@\s._-]+/).filter(Boolean);
  return segments
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase())
    .join('');
}

function displayMember(member: MessagesPanelMember | undefined, userId: string): string {
  return member?.email ?? userId;
}

export function MessagesPage(props: MessagesPanelProps) {
  const {
    apiUrl,
    accessToken,
    tenantId,
    currentUserId,
    currentUserEmail,
    members: membersProp,
    isPlatformAdmin,
    tenantRoles,
    timeZone,
    initialConversationId,
    onInitialConversationConsumed,
    onViewed,
  } = props;

  const [conversations, setConversations] = useState<TenantConversation[]>([]);
  const [broadcasts, setBroadcasts] = useState<PlatformBroadcastMessage[]>([]);
  const [messageParticipants, setMessageParticipants] = useState<MessagesPanelMember[]>(
    membersProp ?? [],
  );
  const [messages, setMessages] = useState<TenantMessage[]>([]);
  const [messageReadMarkers, setMessageReadMarkers] = useState<MessageReadMarker[]>([]);
  const [readReceiptsEnabled, setReadReceiptsEnabled] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [selectedBroadcastId, setSelectedBroadcastId] = useState('');
  const [search, setSearch] = useState('');
  const [composer, setComposer] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newParticipants, setNewParticipants] = useState<string[]>([]);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastPreviewOpen, setBroadcastPreviewOpen] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState<'published' | 'draft' | 'scheduled'>(
    'published',
  );
  const [broadcastScheduledFor, setBroadcastScheduledFor] = useState('');
  const [broadcastExpiresAt, setBroadcastExpiresAt] = useState('');
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [reportingMessageId, setReportingMessageId] = useState('');
  const [reportReason, setReportReason] = useState<'spam' | 'harassment' | 'privacy' | 'other'>(
    'spam',
  );
  const [reportDetails, setReportDetails] = useState('');

  const requestGenerationRef = useRef(0);
  const requestControllersRef = useRef(new Set<AbortController>());

  useEffect(() => {
    requestGenerationRef.current += 1;
    for (const controller of requestControllersRef.current) controller.abort();
    requestControllersRef.current.clear();

    setConversations([]);
    setBroadcasts([]);
    setMessageParticipants(membersProp ?? []);
    setMessages([]);
    setMessageReadMarkers([]);
    setReadReceiptsEnabled(false);
    setMessagesLoading(false);
    setMessagesError('');
    setSelectedConversationId('');
    setSelectedBroadcastId('');

    void loadMessaging();

    return () => {
      requestGenerationRef.current += 1;
      for (const controller of requestControllersRef.current) controller.abort();
      requestControllersRef.current.clear();
    };
  }, [accessToken, apiUrl, tenantId]);

  // Mark messages as viewed when the page is opened (Issue #6)
  const viewedFiredRef = useRef(false);
  useEffect(() => {
    viewedFiredRef.current = false;
  }, [tenantId, currentUserId]);
  useEffect(() => {
    if (conversations.length === 0) return;
    const lastViewedKey = `messages-last-viewed-${tenantId}-${currentUserId}`;
    localStorage.setItem(lastViewedKey, new Date().toISOString());
    if (!viewedFiredRef.current) {
      viewedFiredRef.current = true;
      onViewed?.();
    }
  }, [conversations, tenantId, currentUserId]);

  // Auto-select conversation when navigating from dropdown (Issue #7)
  useEffect(() => {
    if (!initialConversationId || conversations.length === 0) return;
    const target = conversations.find((item) => item.id === initialConversationId);
    if (target) {
      setSelectedConversationId(target.id);
      void loadConversationMessages(target.id);
    }
    onInitialConversationConsumed?.();
  }, [conversations, initialConversationId]);

  const request = async <T,>(path: string, requestOptions: ApiRequest = {}): Promise<T> => {
    const generation = requestGenerationRef.current;
    const controller = new AbortController();
    requestControllersRef.current.add(controller);
    try {
      const response = await fetch(`${apiUrl}${path}`, {
        method: requestOptions.method ?? 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          ...(requestOptions.body === undefined ? {} : { 'content-type': 'application/json' }),
        },
        ...(requestOptions.body === undefined ? {} : { body: JSON.stringify(requestOptions.body) }),
        signal: controller.signal,
      });
      if (generation !== requestGenerationRef.current) throw supersededRequestError();
      if (!response.ok) {
        const message = await readApiError(response);
        if (generation !== requestGenerationRef.current) throw supersededRequestError();
        throw new Error(message);
      }
      const payload = (await response.json()) as T;
      if (generation !== requestGenerationRef.current) throw supersededRequestError();
      return payload;
    } finally {
      requestControllersRef.current.delete(controller);
    }
  };

  const tenantPath = (resource: string) =>
    `/v1/tenants/${encodeURIComponent(tenantId)}/${resource}`;

  const loadMessaging = async () => {
    const generation = requestGenerationRef.current;
    setMessagesLoading(true);
    setMessagesError('');
    setSelectedConversationId('');
    setSelectedBroadcastId('');
    setMessages([]);
    setMessageReadMarkers([]);
    setReadReceiptsEnabled(false);
    try {
      const [conversationPayload, broadcastPayload, participantPayload] = await Promise.all([
        tenantId
          ? request<{ items: TenantConversation[] }>(tenantPath('conversations'))
          : Promise.resolve({ items: [] as TenantConversation[] }),
        request<{ items: PlatformBroadcastMessage[] }>(
          isPlatformAdmin ? '/v1/platform/messages/manage' : '/v1/platform/messages',
        ),
        tenantId
          ? request<{ items: MessagesPanelMember[] }>(tenantPath('message-participants'))
          : Promise.resolve({ items: [] as MessagesPanelMember[] }),
      ]);
      setConversations(conversationPayload.items);
      setBroadcasts(broadcastPayload.items);
      setMessageParticipants(participantPayload.items);
    } catch (error) {
      if (isAbortError(error) || generation !== requestGenerationRef.current) return;
      setMessagesError(error instanceof Error ? error.message : 'Conversations could not load.');
    } finally {
      if (generation === requestGenerationRef.current) setMessagesLoading(false);
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    const generation = requestGenerationRef.current;
    setMessagesLoading(true);
    setMessagesError('');
    setSelectedConversationId(conversationId);
    setSelectedBroadcastId('');
    try {
      const payload = await request<{
        items: TenantMessage[];
        readReceiptsEnabled?: boolean;
        readMarkers?: MessageReadMarker[];
      }>(tenantPath(`conversations/${encodeURIComponent(conversationId)}/messages`));
      setMessages(payload.items);
      setReadReceiptsEnabled(Boolean(payload.readReceiptsEnabled));
      let markers = payload.readMarkers ?? [];
      if (payload.readReceiptsEnabled) {
        const unread = payload.items.filter(
          (message) =>
            message.senderUserId !== currentUserId &&
            !markers.some(
              (marker) => marker.messageId === message.id && marker.userId === currentUserId,
            ),
        );
        const newlyRead = await Promise.all(
          unread.map((message) =>
            request<MessageReadMarker>(
              tenantPath(
                `conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(message.id)}/read`,
              ),
              { method: 'PUT' },
            ),
          ),
        );
        markers = [...markers, ...newlyRead];
      }
      setMessageReadMarkers(markers);
    } catch (error) {
      if (isAbortError(error) || generation !== requestGenerationRef.current) return;
      setMessagesError(error instanceof Error ? error.message : 'Messages could not load.');
    } finally {
      if (generation === requestGenerationRef.current) setMessagesLoading(false);
    }
  };

  const memberById = useMemo(
    () => new Map(messageParticipants.map((member) => [member.userId, member])),
    [messageParticipants],
  );
  const activeMembers = messageParticipants.filter(
    (member) =>
      (member.status === undefined || member.status === 'active') &&
      member.userId !== currentUserId,
  );
  const selectedConversation = conversations.find((item) => item.id === selectedConversationId);
  const selectedBroadcast = broadcasts.find((item) => item.id === selectedBroadcastId);
  const canModerate = tenantRoles?.some((role) => ['tenant_admin', 'manager'].includes(role));

  const conversationName = (conversation: TenantConversation): string => {
    if (conversation.title) return conversation.title;
    const otherParticipants = conversation.participantUserIds.filter(
      (userId) => userId !== currentUserId,
    );
    return (
      otherParticipants.map((userId) => displayMember(memberById.get(userId), userId)).join(', ') ||
      'Conversation'
    );
  };

  const filteredConversations = conversations.filter((conversation) =>
    `${conversationName(conversation)} ${conversation.participantUserIds
      .map((userId) => displayMember(memberById.get(userId), userId))
      .join(' ')}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const filteredBroadcasts = broadcasts.filter((broadcast) =>
    `${broadcast.subject} ${broadcast.body}`.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const sendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedConversationId) return;
    setBusy(true);
    setStatus('');
    try {
      await request(
        tenantPath(`conversations/${encodeURIComponent(selectedConversationId)}/messages`),
        { method: 'POST', body: { body: composer } },
      );
      setComposer('');
      await loadConversationMessages(selectedConversationId);
    } catch (sendError) {
      setStatus(sendError instanceof Error ? sendError.message : 'Message could not send.');
    } finally {
      setBusy(false);
    }
  };

  const createConversation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setStatus('');
    try {
      const payload = await request<{ conversation: TenantConversation }>(
        tenantPath('conversations'),
        {
          method: 'POST',
          body: {
            ...(newTitle.trim() ? { title: newTitle } : {}),
            participantUserIds: newParticipants,
            ...(newMessage.trim() ? { initialMessage: newMessage } : {}),
          },
        },
      );
      setNewTitle('');
      setNewMessage('');
      setNewParticipants([]);
      await loadMessaging();
      await loadConversationMessages(payload.conversation.id);
    } catch (createError) {
      setStatus(
        createError instanceof Error ? createError.message : 'Conversation could not start.',
      );
    } finally {
      setBusy(false);
    }
  };

  const publishBroadcast = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setStatus('');
    try {
      await request('/v1/platform/messages', {
        method: 'POST',
        body: {
          subject: broadcastSubject,
          body: broadcastBody,
          status: broadcastStatus,
          ...(broadcastStatus === 'scheduled' && broadcastScheduledFor
            ? { scheduledFor: toIso(broadcastScheduledFor, timeZone) }
            : {}),
          ...(broadcastExpiresAt ? { expiresAt: toIso(broadcastExpiresAt, timeZone) } : {}),
          isTest: false,
        },
      });
      setBroadcastSubject('');
      setBroadcastBody('');
      setBroadcastPreviewOpen(false);
      setBroadcastStatus('published');
      setBroadcastScheduledFor('');
      setBroadcastExpiresAt('');
      setStatus('Whole-of-app message published.');
      setTimeout(() => setBroadcastModalOpen(false), 2500);
      await loadMessaging();
    } catch (publishError) {
      setStatus(
        publishError instanceof Error ? publishError.message : 'Broadcast could not publish.',
      );
    } finally {
      setBusy(false);
    }
  };

  const previewBroadcast = async () => {
    setBusy(true);
    setStatus('');
    try {
      await request('/v1/platform/messages/preview', {
        method: 'POST',
        body: {
          subject: broadcastSubject,
          body: broadcastBody,
          status: broadcastStatus,
          ...(broadcastStatus === 'scheduled' && broadcastScheduledFor
            ? { scheduledFor: toIso(broadcastScheduledFor, timeZone) }
            : {}),
          ...(broadcastExpiresAt ? { expiresAt: toIso(broadcastExpiresAt, timeZone) } : {}),
          isTest: false,
        },
      });
      setBroadcastPreviewOpen(true);
      setStatus('Preview generated. Nothing has been delivered.');
    } catch (previewError) {
      setStatus(previewError instanceof Error ? previewError.message : 'Preview could not load.');
    } finally {
      setBusy(false);
    }
  };

  const sendBroadcastTest = async () => {
    setBusy(true);
    setStatus('');
    try {
      await request('/v1/platform/messages', {
        method: 'POST',
        body: {
          subject: broadcastSubject,
          body: broadcastBody,
          status: 'published',
          isTest: true,
        },
      });
      setStatus('Test message saved for your administrator account only.');
      await loadMessaging();
    } catch (testError) {
      setStatus(testError instanceof Error ? testError.message : 'Test message could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const reportMessage = async (message: TenantMessage) => {
    setBusy(true);
    setStatus('');
    try {
      await request(
        tenantPath(
          `conversations/${encodeURIComponent(message.conversationId)}/messages/${encodeURIComponent(message.id)}/reports`,
        ),
        {
          method: 'POST',
          body: {
            reason: reportReason,
            ...(reportDetails.trim() ? { details: reportDetails.trim() } : {}),
          },
        },
      );
      setReportingMessageId('');
      setReportDetails('');
      setStatus('Message reported to the organisation administrators.');
    } catch (reportError) {
      setStatus(
        reportError instanceof Error ? reportError.message : 'Message could not be reported.',
      );
    } finally {
      setBusy(false);
    }
  };

  const moderateMessage = async (message: TenantMessage) => {
    setBusy(true);
    setStatus('');
    try {
      await request(
        tenantPath(
          `conversations/${encodeURIComponent(message.conversationId)}/messages/${encodeURIComponent(message.id)}/moderate`,
        ),
        {
          method: 'PATCH',
          body: {
            moderationStatus: message.moderationStatus === 'hidden' ? 'visible' : 'hidden',
          },
        },
      );
      await loadConversationMessages(message.conversationId);
      setStatus(message.moderationStatus === 'hidden' ? 'Message restored.' : 'Message hidden.');
    } catch (moderationError) {
      setStatus(
        moderationError instanceof Error ? moderationError.message : 'Message could not update.',
      );
    } finally {
      setBusy(false);
    }
  };

  const withdrawBroadcast = async (broadcast: PlatformBroadcastMessage) => {
    setBusy(true);
    setStatus('');
    try {
      await request(`/v1/platform/messages/${encodeURIComponent(broadcast.id)}`, {
        method: 'PATCH',
        body: { status: 'withdrawn' },
      });
      setStatus('Whole-of-app message withdrawn.');
      setSelectedBroadcastId('');
      await loadMessaging();
    } catch (withdrawError) {
      setStatus(
        withdrawError instanceof Error ? withdrawError.message : 'Message could not be withdrawn.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="messages-page-container">
      <header className="messages-page-header">
        <h1>Messages</h1>
        <div className="messages-page-header-actions">
          <label className="messages-search">
            <span className="visually-hidden">Search conversations</span>
            {searchIcon()}
            <input
              placeholder="Search conversations"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          {isPlatformAdmin ? (
            <button
              className="messages-broadcast-button"
              type="button"
              onClick={() => setBroadcastModalOpen(true)}
            >
              Whole of App
            </button>
          ) : null}
        </div>
      </header>
      <div className="messages-workspace">
        <aside aria-label="Conversation navigation" className="messages-sidebar">
          {activeMembers.length > 0 ? (
            <form
              className="new-conversation-form"
              onSubmit={(event) => void createConversation(event)}
            >
              <h3>New conversation</h3>
              <input
                aria-label="Conversation title"
                maxLength={160}
                placeholder="Optional title"
                value={newTitle}
                onChange={(event) => setNewTitle(event.target.value)}
              />
              <fieldset>
                <legend>People</legend>
                {activeMembers.map((member) => (
                  <label key={member.userId}>
                    <input
                      checked={newParticipants.includes(member.userId)}
                      type="checkbox"
                      onChange={(event) =>
                        setNewParticipants((current) =>
                          event.target.checked
                            ? [...current, member.userId]
                            : current.filter((userId) => userId !== member.userId),
                        )
                      }
                    />
                    {member.email}
                  </label>
                ))}
              </fieldset>
              <textarea
                aria-label="Optional first message"
                maxLength={10000}
                placeholder="Optional first message"
                rows={2}
                value={newMessage}
                onChange={(event) => setNewMessage(event.target.value)}
              />
              <button disabled={busy || newParticipants.length === 0} type="submit">
                Start conversation
              </button>
            </form>
          ) : null}

          <div aria-label="Conversations" className="conversation-list" role="region">
            {filteredBroadcasts.map((broadcast) => (
              <button
                aria-current={selectedBroadcastId === broadcast.id ? 'true' : undefined}
                className="conversation-list-item platform-message-list-item"
                key={broadcast.id}
                type="button"
                onClick={() => {
                  setSelectedBroadcastId(broadcast.id);
                  setSelectedConversationId('');
                  setMessages([]);
                }}
              >
                <span className="conversation-avatar">QV</span>
                <span>
                  <strong>{broadcast.subject}</strong>
                  <small>
                    Whole of app · {broadcast.status} · v{broadcast.version} ·{' '}
                    {formatDate(broadcast.createdAt)}
                  </small>
                </span>
              </button>
            ))}
            {filteredConversations.map((conversation) => {
              const name = conversationName(conversation);
              return (
                <button
                  aria-current={selectedConversationId === conversation.id ? 'true' : undefined}
                  className="conversation-list-item"
                  key={conversation.id}
                  type="button"
                  onClick={() => void loadConversationMessages(conversation.id)}
                >
                  <span className="conversation-avatar">{initials(name)}</span>
                  <span>
                    <strong>{name}</strong>
                    <small>{formatDate(conversation.updatedAt)}</small>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section aria-label="Message thread" className="message-thread">
          {messagesLoading ? <p className="message-thread-placeholder">Loading messages…</p> : null}
          {!messagesLoading && selectedBroadcast ? (
            <article className="platform-broadcast-detail">
              <span className="utility-scope-label">Whole of app</span>
              <h3>{selectedBroadcast.subject}</h3>
              <time dateTime={selectedBroadcast.createdAt}>
                {formatDate(selectedBroadcast.createdAt)}
              </time>
              <p>{selectedBroadcast.body}</p>
              {isPlatformAdmin && selectedBroadcast.status !== 'withdrawn' ? (
                <button
                  className="utility-danger-button"
                  disabled={busy}
                  type="button"
                  onClick={() => void withdrawBroadcast(selectedBroadcast)}
                >
                  Withdraw message
                </button>
              ) : null}
            </article>
          ) : null}
          {!messagesLoading && selectedConversation ? (
            <>
              <header className="message-thread-header">
                <h3>{conversationName(selectedConversation)}</h3>
                <p>
                  {selectedConversation.participantUserIds
                    .map((userId) =>
                      userId === currentUserId
                        ? (currentUserEmail ?? 'You')
                        : displayMember(memberById.get(userId), userId),
                    )
                    .join(', ')}
                </p>
              </header>
              <div
                aria-atomic="false"
                aria-live="polite"
                aria-relevant="additions text"
                className="message-list"
                role="log"
              >
                {messages.length === 0 ? <p>No messages yet.</p> : null}
                {messages.map((message) => {
                  const sentByCurrentUser = message.senderUserId === currentUserId;
                  const readByCount = messageReadMarkers.filter(
                    (marker) => marker.messageId === message.id && marker.userId !== currentUserId,
                  ).length;
                  return (
                    <article className={sentByCurrentUser ? 'message-own' : ''} key={message.id}>
                      <strong>
                        {sentByCurrentUser
                          ? 'You'
                          : displayMember(
                              memberById.get(message.senderUserId),
                              message.senderUserId,
                            )}
                      </strong>
                      <p>
                        {message.moderationStatus === 'hidden'
                          ? 'This message has been hidden by an organisation moderator.'
                          : message.body}
                      </p>
                      <time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time>
                      {sentByCurrentUser ? (
                        <small>
                          {readReceiptsEnabled && readByCount > 0
                            ? `Read by ${readByCount}`
                            : message.deliveryStatus === 'delivered'
                              ? 'Delivered'
                              : 'Accepted'}
                        </small>
                      ) : null}
                      <div className="message-item-actions">
                        {!sentByCurrentUser && message.moderationStatus !== 'hidden' ? (
                          <button type="button" onClick={() => setReportingMessageId(message.id)}>
                            Report
                          </button>
                        ) : null}
                        {canModerate ? (
                          <button type="button" onClick={() => void moderateMessage(message)}>
                            {message.moderationStatus === 'hidden' ? 'Restore' : 'Hide'}
                          </button>
                        ) : null}
                      </div>
                      {reportingMessageId === message.id ? (
                        <div className="message-report-form">
                          <label>
                            Reason
                            <select
                              value={reportReason}
                              onChange={(event) =>
                                setReportReason(event.target.value as typeof reportReason)
                              }
                            >
                              <option value="spam">Spam</option>
                              <option value="harassment">Harassment</option>
                              <option value="privacy">Privacy concern</option>
                              <option value="other">Other</option>
                            </select>
                          </label>
                          <label>
                            Details <span>(optional)</span>
                            <textarea
                              maxLength={1000}
                              rows={2}
                              value={reportDetails}
                              onChange={(event) => setReportDetails(event.target.value)}
                            />
                          </label>
                          <button
                            disabled={busy}
                            type="button"
                            onClick={() => void reportMessage(message)}
                          >
                            Submit report
                          </button>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
              <form className="message-composer" onSubmit={(event) => void sendMessage(event)}>
                <label className="visually-hidden" htmlFor="messages-page-composer-input">
                  Message
                </label>
                <textarea
                  id="messages-page-composer-input"
                  maxLength={10000}
                  placeholder="Write a message"
                  required
                  rows={2}
                  value={composer}
                  onChange={(event) => setComposer(event.target.value)}
                />
                <button aria-label="Send message" disabled={busy} type="submit">
                  {sendIcon()}
                  <span>Send</span>
                </button>
              </form>
            </>
          ) : null}
          {!messagesLoading && !selectedConversation && !selectedBroadcast ? (
            <p className="message-thread-placeholder">Select a conversation to view messages.</p>
          ) : null}
        </section>
        <StatusMessage error={Boolean(messagesError)}>{messagesError || status}</StatusMessage>
      </div>

      {broadcastModalOpen && isPlatformAdmin ? (
        <Modal label="Whole of App Message" onClose={() => setBroadcastModalOpen(false)}>
          <div className="app-modal-content">
            <form className="utility-editor" onSubmit={(event) => void publishBroadcast(event)}>
              <h3>Whole-of-app message</h3>
              <label>
                Subject
                <input
                  maxLength={160}
                  required
                  value={broadcastSubject}
                  onChange={(event) => setBroadcastSubject(event.target.value)}
                />
              </label>
              <label>
                Delivery
                <select
                  value={broadcastStatus}
                  onChange={(event) =>
                    setBroadcastStatus(event.target.value as typeof broadcastStatus)
                  }
                >
                  <option value="published">Publish now</option>
                  <option value="scheduled">Schedule</option>
                  <option value="draft">Save draft</option>
                </select>
              </label>
              {broadcastStatus === 'scheduled' ? (
                <label>
                  Publish at
                  <input
                    required
                    type="datetime-local"
                    value={broadcastScheduledFor}
                    onChange={(event) => setBroadcastScheduledFor(event.target.value)}
                  />
                </label>
              ) : null}
              <label>
                Expires <span>(optional)</span>
                <input
                  type="datetime-local"
                  value={broadcastExpiresAt}
                  onChange={(event) => setBroadcastExpiresAt(event.target.value)}
                />
              </label>
              <label>
                Message
                <textarea
                  maxLength={10000}
                  required
                  rows={4}
                  value={broadcastBody}
                  onChange={(event) => setBroadcastBody(event.target.value)}
                />
              </label>
              {broadcastPreviewOpen ? (
                <article
                  aria-label="Whole-of-app message preview"
                  className="broadcast-preview"
                  id="broadcast-modal-preview"
                >
                  <span className="utility-scope-label">Preview · Whole of app</span>
                  <h4>{broadcastSubject || 'Message subject'}</h4>
                  <p>{broadcastBody || 'Message content will appear here.'}</p>
                </article>
              ) : null}
              <div className="utility-item-actions">
                <button
                  aria-controls="broadcast-modal-preview"
                  aria-expanded={broadcastPreviewOpen}
                  className="utility-secondary-button"
                  type="button"
                  onClick={() =>
                    broadcastPreviewOpen ? setBroadcastPreviewOpen(false) : void previewBroadcast()
                  }
                >
                  {broadcastPreviewOpen ? 'Hide preview' : 'Preview'}
                </button>
                <button disabled={busy} type="button" onClick={() => void sendBroadcastTest()}>
                  Send test to me
                </button>
                <button disabled={busy || !broadcastPreviewOpen} type="submit">
                  Publish to all users
                </button>
              </div>
              {!broadcastPreviewOpen ? (
                <p className="utility-status">Preview the message before publishing.</p>
              ) : null}
              <StatusMessage>{status}</StatusMessage>
            </form>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
